import React, { useEffect, useState } from "react";
import { Card, Container, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import Axios from "../../../axios/axios";

import { Link } from "react-router-dom";
import LoadingPage from "../../Loader/LoadingPage";
import TopNavBar from "./TopNavBar";
import { Helmet } from "react-helmet-async";

function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);

  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [region, setRegion] = useState("");
  const [year, setYear] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const [filtersChanged, setFiltersChanged] = useState(false);

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "38px",
      borderRadius: "6px",
      borderColor: "#ced4da",
      fontSize: "14px",
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };

  useEffect(() => {
    setIsLoading(true);
    const businessType = sessionStorage.getItem("businessType");

    const fetchProductsAndFilters = async () => {
      let url = "";
      let typeKey = "";

      if (businessType === "Auto-Spare") {
        url = "/spareproducts";
        typeKey = "spare";
      } else if (businessType === "Engineering") {
        url = "/engproducts";
        typeKey = "engineering";
      } else if (businessType === "Building") {
        url = "/bldproducts";
        typeKey = "building";
      } else if (businessType === "Rental") {
        url = "/rentalproducts";
        typeKey = "rental";
      } else {
        setError("Invalid or missing business type.");
        setLoading(false);
        setIsLoading(false);
        return;
      }

      try {
        const [productRes, typeDetailsRes, categoryRes] = await Promise.all([
          Axios.get(
            `${url}?page=${pagination.currentPage}&limit=${pagination.itemsPerPage}`
          ),
          Axios.get("/type-based-details"),
          Axios.get(`/categories?type=${typeKey}`),
        ]);

        const responseData = productRes.data || {};
        const data = responseData.data || [];
        const paginationData = responseData.pagination || {
          totalPages: 1,
          totalItems: 0,
        };

        const normalized = data.map((item) => ({
          id: item._id,
          name:
            item.partName ||
            item.name ||
            item.meterialName ||
            item.rentalItem ||
            "Unnamed",
          images: item.images || item.photos || [],
          brand: item.brandId?._id || item.brandId || "",
          model: item.modelId?._id || item.modelId || "",
          category: item.categoryId?._id || item.categoryId || "",
          partNo: item.partNo,
          year: item.year,
          engine: item.engine || item.material || item.meterialName || "",
          productType:
            item.productType?.join(", ") || item.rentalType?.join(", ") || "",
          region: item.regionId || "",
          rentPricePerDay: item.rentPricePerDay || null,
          businessType: businessType,
        }));

        setProducts(normalized);

        if (!category && !brand && !model && !region && !year) {
          setFilteredProducts(normalized);
        }

        setPagination({
          ...pagination,
          totalPages: paginationData.totalPages,
          totalItems: paginationData.totalItems,
        });

        const filters = typeDetailsRes.data[typeKey];

        if (filters) {
          setBrandOptions(filters.brands || []);
          setModelOptions(filters.models || []);
        } else {
          setBrandOptions([]);
          setModelOptions([]);
        }

        if (Array.isArray(categoryRes.data)) {
          const target = typeKey.toLowerCase();
          const filtered = categoryRes.data.filter((c) => {
            if (!c.type || c.type.length === 0) return true;
            return Array.isArray(c.type)
              ? c.type.some((t) => t.toLowerCase() === target)
              : String(c.type).toLowerCase() === target;
          });
          setCategoryOptions(filtered);
        }

        const uniqueRegions = [
          ...new Map(
            data
              .map((item) => item.regionId)
              .filter((r) => r && r._id)
              .map((r) => [r._id, r])
          ).values(),
        ];
        setRegionOptions(uniqueRegions);

        const uniqueYears = [
          ...new Set(data.map((item) => item.year).filter(Boolean)),
        ];
        setYearOptions(uniqueYears);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to fetch products. Please try again later.");
      } finally {
        setLoading(false);
        setIsLoading(false);
      }
    };

    fetchProductsAndFilters();
  }, [pagination.currentPage, pagination.itemsPerPage, filtersChanged]);

  useEffect(() => {
    let filtered = [...products];

    if (category) {
      filtered = filtered.filter(
        (product) => String(product.category) === String(category)
      );
    }

    if (brand) {
      filtered = filtered.filter((product) => String(product.brand) === String(brand));
    }

    if (model) {
      filtered = filtered.filter((product) => String(product.model) === String(model));
    }

    if (region) {
      filtered = filtered.filter(
        (product) => String(product.region?._id || product.region) === String(region)
      );
    }

    if (year) {
      filtered = filtered.filter((product) => String(product.year) === String(year));
    }

    setFilteredProducts(filtered);
  }, [category, brand, model, region, year, products]);

  const handleBrandChange = (opt) => {
    const selectedBrandId = opt ? opt.value : "";
    setBrand(selectedBrandId);
    setModel("");
  };

  const handlePageChange = (pageNumber) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: pageNumber,
    }));
  };

  if (loading) return <LoadingPage />;

  const categorySelectOpts = (categoryOptions || []).map((c) => ({
    value: c._id,
    label: c.name,
  }));

  const brandSelectOpts = (brandOptions || []).map((b) => ({
    value: b._id,
    label: b.name,
  }));

  const modelSelectOpts = (modelOptions || [])
    .filter((m) => !brand || String(m.brandId?._id || m.brandId) === String(brand))
    .map((m) => ({
      value: m._id,
      label: m.name,
    }));

  const regionSelectOpts = (regionOptions || []).map((r) => ({
    value: r._id,
    label: r.name,
  }));

  const yearSelectOpts = (yearOptions || []).map((y) => ({
    value: String(y),
    label: String(y),
  }));

  return (
    <div>
      <Helmet>
        <title>Auto Spare Parts Online | Asia Parts Souq</title>
        <meta
          name="description"
          content={
            brand || model || category
              ? "Find spare parts for selected category, brand and model. Filter by year and region."
              : "Browse genuine auto spare parts by category, brand, model, region and year."
          }
        />
      </Helmet>

      <div className="Main-div">
        <TopNavBar />
        <section id="products-section">
          <Container className="">
            <div className="d-flex justify-content-between align-items-center m-4 p-5">
              <h2 className="fw-bold">Our Products</h2>
            </div>

            {/* Search and Filter Bar */}
            <div className="mb-4 p-3 rounded-3">
              <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 g-2">
                <div className="col">
                  <Select
                    isClearable
                    isSearchable
                    placeholder="Category"
                    options={categorySelectOpts}
                    value={
                      categorySelectOpts.find((o) => o.value === category) || null
                    }
                    onChange={(opt) => setCategory(opt ? opt.value : "")}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="col">
                  <Select
                    isClearable
                    isSearchable
                    placeholder="Brand"
                    options={brandSelectOpts}
                    value={brandSelectOpts.find((o) => o.value === brand) || null}
                    onChange={handleBrandChange}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="col">
                  <Select
                    isClearable
                    isSearchable
                    placeholder="Model"
                    options={modelSelectOpts}
                    value={modelSelectOpts.find((o) => o.value === model) || null}
                    onChange={(opt) => setModel(opt ? opt.value : "")}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="col">
                  <Select
                    isClearable
                    isSearchable
                    placeholder="Region"
                    options={regionSelectOpts}
                    value={regionSelectOpts.find((o) => o.value === region) || null}
                    onChange={(opt) => setRegion(opt ? opt.value : "")}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="col">
                  <Select
                    isClearable
                    isSearchable
                    placeholder="Year"
                    options={yearSelectOpts}
                    value={yearSelectOpts.find((o) => o.value === String(year)) || null}
                    onChange={(opt) => setYear(opt ? opt.value : "")}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              {isLoading && (
                <div className="d-flex justify-content-center mt-3">
                  <Spinner animation="border" variant="danger" />
                </div>
              )}
            </div>

            {/* Product Grid */}
            <Row xs={1} md={2} lg={3} xl={5} className="g-4">
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <Col key={product.id}>
                    <Card className="h-100 border-0 shadow-sm hover-shadow">
                      <div className="position-relative overflow-hidden">
                        <Card.Img
                          variant="top"
                          src={product.images[0]?.url || "/images/no-image.jpg"}
                          alt={product.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/images/download.jpeg";
                          }}
                          style={{ height: "200px", objectFit: "cover" }}
                        />
                      </div>
                      <Link
                        to={
                          product.businessType === "Auto-Spare"
                            ? `/singleproduct/${product.id}`
                            : product.businessType === "Engineering"
                              ? `/engineering/product/single/${product.id}`
                              : product.businessType === "Building"
                                ? `/others/product/single/${product.id}`
                                : product.businessType === "Rental"
                                  ? `/rental/product/single/${product.id}`
                                  : "#"
                        }
                        style={{
                          textDecoration: "none",
                          color: "#1A54A5",
                          fontWeight: "bolder",
                        }}
                      >
                        <Card.Body className="d-flex flex-column">
                          <Card.Title className="fs-6 mb-2">
                            <div className=" ">
                              <h5>{product?.name}</h5>
                              <div className="d-flex flex-row">
                                <div className="text-danger mb-1 me-2">
                                  <i className="fa fa-star"></i>
                                  <i className="fa fa-star"></i>
                                  <i className="fa fa-star"></i>
                                </div>
                              </div>
                              <div className="mt-1 mb-0 text-muted small">
                                <span>{product.partNo}</span>
                                <span className="text-primary"> • </span>
                                <span>{product.year}</span>
                                <span className="text-primary"> • </span>
                                <span>{product.engine}</span>
                              </div>
                              <div className="mb-2 text-muted small">
                                <span>{product.productType}</span>
                                <span className="text-primary"> • </span>
                                <span>{product.region?.name}</span>
                              </div>
                            </div>
                          </Card.Title>
                        </Card.Body>
                      </Link>
                    </Card>
                  </Col>
                ))
              ) : (
                !isLoading && (
                  <Col xs={12} className="text-center py-5">
                    <h4>No products found matching your filters</h4>
                  </Col>
                )
              )}
            </Row>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-center mt-5">
                <nav>
                  <ul className="pagination">
                    <li
                      className={`page-item ${
                        pagination.currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage - 1)
                        }
                      >
                        Previous
                      </button>
                    </li>

                    {(() => {
                      const pages = [];
                      const { currentPage, totalPages } = pagination;

                      const startPage = Math.max(2, currentPage - 2);
                      const endPage = Math.min(totalPages - 1, currentPage + 2);

                      pages.push(
                        <li
                          key={1}
                          className={`page-item ${
                            currentPage === 1 ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(1)}
                          >
                            1
                          </button>
                        </li>
                      );

                      if (startPage > 2) {
                        pages.push(
                          <li
                            key="start-ellipsis"
                            className="page-item disabled"
                          >
                            <span className="page-link">...</span>
                          </li>
                        );
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <li
                            key={i}
                            className={`page-item ${
                              currentPage === i ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      if (endPage < totalPages - 1) {
                        pages.push(
                          <li key="end-ellipsis" className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }

                      if (totalPages > 1) {
                        pages.push(
                          <li
                            key={totalPages}
                            className={`page-item ${
                              currentPage === totalPages ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(totalPages)}
                            >
                              {totalPages}
                            </button>
                          </li>
                        );
                      }

                      return pages;
                    })()}

                    <li
                      className={`page-item ${
                        pagination.currentPage === pagination.totalPages
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage + 1)
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </Container>
        </section>
      </div>
    </div>
  );
}

export default Products;
